import React, { useState, useRef } from "react";
import {
  X,
  Upload,
  Download,
  AlertCircle,
  CheckCircle,
  XCircle,
} from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const ImportLeadsModal = ({ onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [options, setOptions] = useState({
    skipDuplicates: true,
    updateExisting: false,
  });
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type
      const validTypes = [
        "text/csv",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];

      if (!validTypes.includes(selectedFile.type)) {
        alert("Please upload a CSV or Excel file");
        return;
      }

      // Validate file size (10MB max)
      if (selectedFile.size > 10 * 1024 * 1024) {
        alert("File size must be less than 10MB");
        return;
      }

      setFile(selectedFile);
      setResults(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      alert("Please select a file");
      return;
    }

    try {
      setImporting(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("skipDuplicates", options.skipDuplicates);
      formData.append("updateExisting", options.updateExisting);

      const response = await apiService.importLeads(formData);

      setResults(response);

      if (response.success) {
        setTimeout(() => {
          onSuccess();
        }, 2000);
      }
    } catch (error) {
      console.error("Import error:", error);
      alert(error.response?.data?.message || "Failed to import leads");
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async (format) => {
    try {
      const response = await apiService.downloadLeadTemplate(format);

      // Create blob and download
      const blob = new Blob([response], {
        type:
          format === "xlsx"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "text/csv",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `leads_template.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Template download error:", error);
      alert("Failed to download template");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto flex flex-col ${
          isDarkMode
            ? "bg-[#0C0E12] border border-gray-700"
            : "bg-white border border-gray-300"
        }`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-6 border-b ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div>
            <h2
              className={`text-lg font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Import Leads
            </h2>
            <p
              className={`text-sm mt-1 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              Upload a CSV or Excel file to import leads
            </p>
          </div>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Download Templates */}
          <div
            className={`rounded-lg p-4 ${
              isDarkMode
                ? "bg-blue-500/10 border border-blue-500/30"
                : "bg-blue-50 border border-blue-200"
            }`}
          >
            <div className="flex items-start gap-3">
              <AlertCircle
                size={20}
                className={`flex-shrink-0 mt-0.5 ${
                  isDarkMode ? "text-blue-400" : "text-blue-600"
                }`}
              />
              <div className="flex-1">
                <h3
                  className={`font-medium mb-2 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Download Template
                </h3>
                <p
                  className={`text-sm mb-3 ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Start with our template to ensure your data is formatted
                  correctly
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleDownloadTemplate("csv")}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isDarkMode
                        ? "bg-blue-600 hover:bg-blue-700 text-white"
                        : "bg-blue-500 hover:bg-blue-600 text-white"
                    }`}
                  >
                    <Download size={16} />
                    Download CSV Template
                  </button>
                  <button
                    onClick={() => handleDownloadTemplate("xlsx")}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isDarkMode
                        ? "bg-blue-600 hover:bg-blue-700 text-white"
                        : "bg-blue-500 hover:bg-blue-600 text-white"
                    }`}
                  >
                    <Download size={16} />
                    Download Excel Template
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* File Upload */}
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Upload File
            </label>
            <div
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDarkMode
                  ? "border-gray-700 hover:border-gray-600"
                  : "border-gray-300 hover:border-gray-400"
              }`}
            >
              <Upload
                size={48}
                className={`mx-auto mb-4 ${
                  isDarkMode ? "text-gray-400" : "text-gray-400"
                }`}
              />
              {file ? (
                <div>
                  <p
                    className={`font-medium ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {file.name}
                  </p>
                  <p
                    className={`text-sm mt-1 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      setResults(null);
                    }}
                    className={`text-sm mt-2 ${
                      isDarkMode
                        ? "text-red-400 hover:text-red-300"
                        : "text-red-500 hover:text-red-600"
                    }`}
                  >
                    Remove file
                  </button>
                </div>
              ) : (
                <div>
                  <p
                    className={`mb-1 ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    Click to upload or drag and drop
                  </p>
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-400"
                    }`}
                  >
                    CSV or Excel files up to 10MB
                  </p>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
          </div>

          {/* Options */}
          <div className="space-y-3">
            <label
              className={`block text-sm font-medium ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Import Options
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={options.skipDuplicates}
                onChange={(e) =>
                  setOptions({ ...options, skipDuplicates: e.target.checked })
                }
                className={`w-4 h-4 rounded focus:ring-blue-500 focus:ring-offset-0 ${
                  isDarkMode
                    ? "border-gray-600 bg-gray-700 text-blue-600"
                    : "border-gray-300 bg-white text-blue-600"
                }`}
              />
              <div>
                <span
                  className={`text-sm ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Skip duplicates
                </span>
                <p
                  className={`text-xs ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Don't import leads with existing email addresses
                </p>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={options.updateExisting}
                onChange={(e) =>
                  setOptions({ ...options, updateExisting: e.target.checked })
                }
                disabled={options.skipDuplicates}
                className={`w-4 h-4 rounded focus:ring-blue-500 focus:ring-offset-0 ${
                  isDarkMode
                    ? "border-gray-600 bg-gray-700 text-blue-600 disabled:opacity-50"
                    : "border-gray-300 bg-white text-blue-600 disabled:opacity-50"
                }`}
              />
              <div>
                <span
                  className={`text-sm ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Update existing leads
                </span>
                <p
                  className={`text-xs ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Update data for leads that already exist
                </p>
              </div>
            </label>
          </div>

          {/* Results */}
          {results && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div
                  className={`rounded-lg p-4 ${
                    isDarkMode ? "bg-gray-800" : "bg-gray-100"
                  }`}
                >
                  <div
                    className={`text-2xl font-bold ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {results.total}
                  </div>
                  <div
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Total Rows
                  </div>
                </div>
                <div
                  className={`rounded-lg p-4 ${
                    isDarkMode ? "bg-green-500/10" : "bg-green-50"
                  }`}
                >
                  <div
                    className={`text-2xl font-bold ${
                      isDarkMode ? "text-green-400" : "text-green-600"
                    }`}
                  >
                    {results.imported}
                  </div>
                  <div
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Imported
                  </div>
                </div>
                <div
                  className={`rounded-lg p-4 ${
                    isDarkMode ? "bg-blue-500/10" : "bg-blue-50"
                  }`}
                >
                  <div
                    className={`text-2xl font-bold ${
                      isDarkMode ? "text-blue-400" : "text-blue-600"
                    }`}
                  >
                    {results.updated}
                  </div>
                  <div
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Updated
                  </div>
                </div>
                <div
                  className={`rounded-lg p-4 ${
                    isDarkMode ? "bg-gray-500/10" : "bg-gray-100"
                  }`}
                >
                  <div
                    className={`text-2xl font-bold ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {results.skipped}
                  </div>
                  <div
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Skipped
                  </div>
                </div>
              </div>

              {results.errors && results.errors.length > 0 && (
                <div
                  className={`rounded-lg p-4 ${
                    isDarkMode
                      ? "bg-red-500/10 border border-red-500/30"
                      : "bg-red-50 border border-red-200"
                  }`}
                >
                  <h4
                    className={`font-medium mb-2 flex items-center gap-2 ${
                      isDarkMode ? "text-red-400" : "text-red-600"
                    }`}
                  >
                    <XCircle size={18} />
                    {results.errors.length} Errors
                  </h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {results.errors.slice(0, 10).map((error, index) => (
                      <div
                        key={index}
                        className={`text-sm ${
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        <span
                          className={
                            isDarkMode ? "text-red-400" : "text-red-500"
                          }
                        >
                          Row {error.row}:
                        </span>{" "}
                        {error.error}
                      </div>
                    ))}
                    {results.errors.length > 10 && (
                      <p
                        className={`text-sm italic ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        ... and {results.errors.length - 10} more errors
                      </p>
                    )}
                  </div>
                </div>
              )}

              {results.success && results.errors.length === 0 && (
                <div
                  className={`rounded-lg p-4 ${
                    isDarkMode
                      ? "bg-green-500/10 border border-green-500/30"
                      : "bg-green-50 border border-green-200"
                  }`}
                >
                  <div
                    className={`flex items-center gap-2 ${
                      isDarkMode ? "text-green-400" : "text-green-600"
                    }`}
                  >
                    <CheckCircle size={20} />
                    <span className="font-medium">
                      Import Completed Successfully!
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className={`flex items-center justify-end gap-3 p-6 border-t ${
            isDarkMode
              ? "border-gray-700 bg-[#0C0E12]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg transition-colors ${
              isDarkMode
                ? "bg-gray-700 hover:bg-gray-600 text-white"
                : "bg-gray-200 hover:bg-gray-300 text-gray-900"
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={!file || importing}
            className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${
              isDarkMode
                ? "bg-blue-600 hover:bg-blue-700 text-white"
                : "bg-blue-500 hover:bg-blue-600 text-white"
            }`}
          >
            {importing ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Upload size={16} />
                Import Leads
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportLeadsModal;
